import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { DownloadProjectVM } from './DownloadProjectVM';
import { CollapseTable } from 'cd-eft/components/common/CollapseTable';
import { mdiDownloadOutline } from '@mdi/js';
import { Loader } from 'cd-eft/components/common/LoadingSpinner/LoadingSpinner';
import { Alert } from 'cd-eft/components/common/Alert';
import { appConfig } from 'cd-eft/config/AppConfig';
import { DownloadButton } from 'cd-eft/components/common/DownloadButton';
import { Row, Col, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { StaticTooltip } from 'cd-eft/components/common/StaticTooltip/StaticTooltip';
import { Tabs } from 'cd-eft/components/common/Tabs';
import { SelectOption } from 'cd-eft/components/common/SelectOption';
import { projectClassId } from '../ProjectContainer/ProjectContainerVM';
import { OUTPUT_TYPES } from './DownloadProject.config';
import { PrintConstructionReports } from '../PrintConstructionReports/PrintConstructionReports';
import { PrintComponent } from 'cd-eft/components/common/PrintButton';
import { PrintDesignReports } from '../PrintDesignReports';
import { DownloadProjectImages } from '../DownloadProjectImages/DownloadProjectImages';
import stores from '../../../models/Stores';
import Icon from '@mdi/react';
import './DownloadProject.scss';

const { projectsLibraryStore: libraryStore } = stores.AppStore;

interface IDownloadProjectProps {
  showDownloadPopup: boolean;
  toggleDownloadPopup: () => void;
  project: any;
}

const DownloadProjectComponent = (props: IDownloadProjectProps) => {
  const vm = useRef(new DownloadProjectVM(props)).current;

  useEffect(() => {
    vm.onMount();
  }, []);

  useEffect(() => {
    vm.updateContent();
  }, [libraryStore.isLoading, libraryStore.fileFormat]);

  return (
    <Modal
      className={`eft-action-modal modal-Download`}
      isOpen={props.showDownloadPopup}
      toggle={props.toggleDownloadPopup}
      centered={true}
      labelledBy="Output"
    >
      <ModalHeader tag="h1" toggle={props.toggleDownloadPopup} id="Output">
        Output
        <Icon path={mdiDownloadOutline} title="" size={1} color="#005ea2" />
      </ModalHeader>

      <ModalBody className="download-project-popup-content">
        <div className="project-name-container">
          <strong>Project Name: {props.project.projectTitle}</strong>
        </div>

        <Tabs tabs={vm.tabs} currentTab={vm.currentSubTab} onClick={vm.changeSubTab} />

        <SelectOption
          label={vm.currentTabObject.label}
          idAndName="projectClassId"
          options={vm.currentTabObject.options}
          value={libraryStore.fileFormat}
          onChange={vm.selectFileFormatHandler}
          required={true}
          removeDefaultOption={true}
          onBasicDataForm={true}
        />

        <div className="mt-3 tabpan-container">
          {props.project.projectClassId === projectClassId.hydrology ? (
            libraryStore.fileFormat !== 'AnalysisImages' ? <>
              {vm.validTypes.map((type) => (
                <Info
                  key={`type-${OUTPUT_TYPES[type].label}`}
                  type={type}
                  selectedType={vm.selectedHydrologyType}
                  selectType={vm.selectHydrologyType}
                  onChange={vm.selectHydrologyFile}
                  selected={vm.selectedHydrologyFiles}
                  isOpen={true}
                />
              ))}
            </>
              :
              <CollapseTable
                title={`Analysis - Hydrology Images(${vm.hydrologyImages.length})`}
                bodyContainerId="hydrology-images"
                isOpen={true}
                checked={vm.isAllHydrologyImagesSelected()}
                onChange={() => vm.selectHydrologyImage(undefined)}
              >
                {vm.hydrologyImages.map((element, index) => (
                  <Row key={index} className="hydrology-image-row">
                    <Col>
                      <input
                        id={`checkbox-${index}`}
                        name={`checkbox-${index}`}
                        data-testid={`checkbox-${index}`}
                        type="checkbox"
                        value={`value-${index}`}
                        checked={element.selected}
                        onChange={() => vm.selectHydrologyImage(index)}
                      />
                      <label htmlFor={`checkbox-${index}`}>{element.name}</label>
                    </Col>
                  </Row>
                ))}
              </CollapseTable>

          ) : (
            <>
              {/* DesignElements */}
              {vm.validTypes.map((designTypeId) => {
                const designtype = libraryStore.designElementsByType[designTypeId];
                // Hide Terrace System if it has no design elements
                if (designtype && designtype.designElements && designtype.designElements.length > 0) {
                  const showAdded =
                    libraryStore.fileFormat === 'CSV' &&
                    (designTypeId === 'Waterway' || designTypeId === "Terrace") &&
                    designtype.designElements.some((el) => el.isSelected);
                  if (designTypeId === 10) {
                    return (
                      <div className='collapseTable dem-download-options'>
                        {designtype.designElements &&
                          designtype.designElements.map((designelement) => {
                            const name = designelement.designElementName;
                            const id = designelement.designElementId;
                            return (
                              <React.Fragment key={`${designtype.designElementType}-${id}`}>
                                <Row>
                                  <Col>
                                    <input
                                      id={`${designtype.designElementType}-${id}`}
                                      name={`${designtype.designElementType}-${id}`}
                                      data-testid={`${designtype.designElementType}-${id}`}
                                      type="checkbox"
                                      value={id}
                                      checked={designelement.isSelected}
                                      onChange={(e) => vm.selectDesignElement(e, designelement)}
                                      disabled={true}
                                    />
                                    <label htmlFor={`${designtype.designElementType}-${id}`}>
                                      {name ? name : 'Name-' + id}
                                    </label>
                                  </Col>
                                </Row>
                              </React.Fragment>
                            );
                          })}
                      </div>
                    )
                  }
                  else
                    return (
                      <CollapseTable
                        key={`${designtype.designElementTypeId}-summary`}
                        data-testid={`${designtype.designElementTypeId}-summary`}
                        title={`${designtype.designElementType} ${showAdded ? 'Cross Sections' : ''} (${designtype.size})`}
                        bodyContainerId={`${designtype.designElementTypeId}-summary`}
                        isOpen={libraryStore.fileFormat === 'Design' ? true : false}
                        checked={vm.designTypeIsChecked(designtype)}
                        onChange={(e) => vm.selectAllByTypeHandler(e, designTypeId)}
                        titleAdded={showAdded ? 'Additional Stations' : ''}
                        // checkedAdd={showAdded ? vm.additionalStations : ''}
                        checkedAdd={showAdded && designTypeId === "Terrace" ? vm.additionalStationsTerrace :
                          showAdded && designTypeId === 'Waterway' ? vm.additionalStations : ''
                        }
                        // onChangeAdd={showAdded ? vm.showAddtnlStations : undefined}
                        onChangeAdd={showAdded && designTypeId === "Terrace" ? vm.showAddtnlStationsTerrace :
                          showAdded && designTypeId === 'Waterway' ? vm.showAddtnlStations : undefined
                        }
                        showAdded={showAdded ? true : false}
                        disabled={
                          libraryStore.fileFormat === 'Images'
                            ? !designtype.designElements.some((el) => el.images)
                            : designtype.designElementType === 'Terrace System'
                              ? false  // Never disable Terrace System main checkbox
                              : designtype.designElements.filter(el => el.isDisabled).length === designtype.designElements.length
                        }
                      >

                        {libraryStore.fileFormat === 'Images' &&
                          designTypeId === 'Terrace System' &&
                          designtype.terraceImages &&
                          designtype.terraceImages.map((img) => (
                            <Row key={`TerraceImages-${img.imageId}`}>
                              <Col>
                                <input
                                  id={`TerraceImages-${img.imageId}`}
                                  name={`TerraceImages-${img.imageName}`}
                                  data-testid={`TerraceImages-${img.imageId}`}
                                  type="checkbox"
                                  value={img.imageId}
                                  checked={libraryStore.selectedImages.includes(+img.imageId)}
                                  onChange={(e) => vm.selectImage(e, img)}
                                  disabled={img.isDisabled}
                                />
                                <label htmlFor={`TerraceImages-${img.imageId}`}>
                                  {img.imageName ?? 'Image Type ' + img.imageId}
                                </label>
                              </Col>
                            </Row>
                          ))
                        }

                        {designtype.designElements &&
                          designtype.designElements.map((designelement) => {
                            const name = designelement.designElementName;
                            const id = designelement.designElementId;
                            return (
                              <React.Fragment key={`${designtype.designElementType}-${id}`}>
                                {libraryStore.fileFormat === 'Images' ? (
                                  <DownloadProjectImages
                                    type={designtype}
                                    element={designelement}
                                    someChangesOnScreen={vm.someChangesOnScreen}
                                  />
                                ) : (
                                  <Row>
                                    <Col>
                                      <input
                                        id={`${designtype.designElementType}-${id}`}
                                        name={`${designtype.designElementType}-${id}`}
                                        data-testid={`${designtype.designElementType}-${id}`}
                                        type="checkbox"
                                        value={id}
                                        checked={designelement.isSelected}
                                        onChange={(e) => vm.selectDesignElement(e, designelement)}
                                        disabled={designelement.isDisabled}
                                      />
                                      <label htmlFor={`${designtype.designElementType}-${id}`}>
                                        {name ? name : 'Name-' + id}
                                      </label>

                                      {/* Only render InformationTooltip if flagUpToDate is TRUE */}
                                      {designelement.isDisabled && !designelement.isSelected &&
                                        (designtype.designElementType === 'Terrace System' && libraryStore.fileFormat === 'Construction' ? !designelement.originallyEnabled : true) && (
                                          <StaticTooltip
                                            title="Information"
                                            tooltipMessage={
                                              designelement.flagId
                                                ? `${designelement.name} is disabled from altered input data. To enable downloads, please navigate to the tool and finalize your edits.`
                                                : `${designelement.name} is disabled as design is incomplete. To enable downloads, please navigate to the tool and complete entire workflow of design`
                                            }
                                            tooltipSide="right"
                                            id={`waterway-warnings-tooltip-${id}`}
                                          />
                                        )}
                                    </Col>

                                    {/* for Alignments only */}
                                    {libraryStore.fileFormat === 'CSV' &&
                                      designtype.designElementTypeId ===
                                      appConfig.hardCodes.designElementTypeIndependent &&
                                      designelement.isSelected && (
                                        <Col>
                                          <label htmlFor={`sampleInterval-for-${id}`}>Sample Interval (ft)</label>
                                          <input
                                            type="text"
                                            name={`sampleInterval-for-${id}`}
                                            data-testid={`sampleInterval-for-${id}`}
                                            id={`sampleInterval-for-${id}`}
                                            value={designelement.interval}
                                            placeholder="0"
                                            className="usa-input interval-input"
                                            pattern="[0-9]*"
                                            inputMode="numeric"
                                            onChange={(e) => vm.setInterval(e, designelement)}
                                          />
                                        </Col>
                                      )}

                                    {/* for Waterway CSV */}
                                    {showAdded && designelement.isSelected && vm.additionalStations && designTypeId === 'Waterway' ? (
                                      <Col>
                                        <label
                                          className={!designelement.isDisabled ? 'text-disabled' : ''}
                                          htmlFor={`stationInterval-${id}`}
                                        >
                                          Station Interval (ft)
                                        </label>
                                        <input
                                          type="text"
                                          name={`stationInterval-${id}`}
                                          data-testid={`stationInterval-${id}`}
                                          id={`stationInterval-${id}`}
                                          value={designelement.interval}
                                          placeholder="0"
                                          className="usa-input interval-input"
                                          pattern="[0-9]*"
                                          inputMode="numeric"
                                          onChange={(e) => vm.setIntervalWaterway(e, designelement)}
                                          disabled={designelement.isDisabled}
                                        />
                                      </Col>
                                    ) : null}
                                    {showAdded && designelement.isSelected && vm.additionalStationsTerrace && designTypeId === 'Terrace' ? (
                                      <Col>
                                        <label
                                          className={!designelement.isDisabled ? 'text-disabled' : ''}
                                        //for Terrace CSVs
                                        >
                                          Station Interval (ft)
                                        </label>
                                        <input
                                          type="text"
                                          name={`terracesStationInterval-${id}`}
                                          data-testid={`terracesStationInterval-${id}`}
                                          id={`terracesStationInterval-${id}`}
                                          value={designelement.interval}
                                          placeholder="0"
                                          className="usa-input interval-input terrace-interval-input"
                                          pattern="[0-9]*"
                                          inputMode="numeric"
                                          onChange={(e) => vm.setIntervalTerrace(e, designelement)}
                                          disabled={designelement.isDisabled} //for Terrace CSV
                                        />
                                      </Col>
                                    ) : null}
                                  </Row>
                                )}
                              </React.Fragment>
                            );
                          })}
                      </CollapseTable>
                    );
                }
              })}

              {vm.getTotalAvailableElements() === 0 && (
                <div className="py-4 text-center">Project doesn't have data for this output type</div>
              )}
            </>
          )}
        </div>

        {vm.formValidation.formErrors.selectedElements && (
          <div className="usa-error-message" id="client--message" role="alert">
            {vm.formValidation.formErrors.selectedElements}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        {vm.successGeneratingFile && (
          <Alert show={vm.successGeneratingFile} type="success" title={<SuccessMessage />} />
        )}

        {props.project.projectClassId === projectClassId.design ? (
          <div className="button-group">
            <div>
              <button
                data-testid="selectButtonId"
                className="usa-button"
                onClick={vm.selectAllHandler}
                disabled={vm.getTotalAvailableElements() === 0}
              >
                Select All
              </button>
              <button
                data-testid="clearButtonId"
                className="usa-button usa-button--outline"
                onClick={vm.clearAllHandler}
                disabled={vm.getTotalAvailableElements() === 0}
              >
                Clear All
              </button>
            </div>

            <div>
              <button className="usa-button usa-button--outline" onClick={vm.cancelHandler}>
                Close
              </button>
              {vm.successGeneratingFile ? (
                <button className="usa-button" onClick={vm.downloadHandler}>
                  Download
                </button>
              ) : (
                <>
                  {libraryStore.fileFormat === 'Construction' ? (
                    <PrintComponent
                      componentToPrint={PrintConstructionReports}
                      printButtonText={'Submit'}
                      ButtonType={''}
                      componentProps={{ ...props }}
                      disabled={vm.countByFormat === 0}
                    />
                  ) : libraryStore.fileFormat === 'Design' ? (
                    <PrintComponent
                      componentToPrint={PrintDesignReports}
                      printButtonText={'Submit'}
                      ButtonType={''}
                      componentProps={{ project: props.project }}
                      disabled={vm.countByFormat === 0}
                    />
                  ) : (
                    <button className="usa-button" onClick={vm.submitHandler} disabled={vm.countByFormat === 0}>
                      Submit
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ) : props.project.projectClassId === projectClassId.hydrology ? (
          <>
            <div></div>
            <div>
              <button className="usa-button usa-button--outline" onClick={vm.cancelHandler}>
                Close
              </button>
              {vm.selectedHydrologyType === 'DetailFiles' && (
                <DownloadButton
                  href=""
                  download={`NETS_Hydrology_Details_${vm.getDate()}.zip`}
                  createLink={vm.downloadDetails}
                >
                  Download
                </DownloadButton>
              )}
              {vm.selectedHydrologyType === 'GISFiles' && (
                <DownloadButton
                  href=""
                  download={`NETS_Hydrology_GIS_${vm.project.projectTitle}_${vm.getDate()}.zip`}
                  createLink={vm.getProjectAttachments}
                >
                  Download
                </DownloadButton>
              )}
              {vm.selectedHydrologyType === 'AdditionalInformation' && (
                <DownloadButton
                  href=""
                  download={`NETS_Hydrology_DetailReport_${vm.getDate()}.pdf`}
                  createLink={vm.downloadReport}
                >
                  Download
                </DownloadButton>
              )}
              {libraryStore.fileFormat === 'AnalysisImages' && vm.hydrologyImages.length > 0 && (
                <button onClick={vm.downloadImages} className='usa-button' disabled={!vm.hydrologyImages.some(e => e.selected)}>
                  Download
                </button>
              )}
            </div>
          </>
        ) : null}
      </ModalFooter>

      {vm.isLoading && <Loader message={vm.loadingMsg} />}
    </Modal>
  );
};
export const DownloadProject = observer(DownloadProjectComponent);

const SuccessMessage = observer(() => (
  <>
    <div>New file created!</div>
    <div className="mt-2">You can download the project.</div>
  </>
));

const Info = observer(({ type, onChange, selected, isOpen, selectedType, selectType }) => {
  const label = OUTPUT_TYPES[type].label;
  const options = OUTPUT_TYPES[type].options;
  return (
    <>
      {options && options.length > 0 ? (
        <CollapseTable
          title={label}
          bodyContainerId={type}
          value={type}
          isOpen={isOpen ? isOpen : false}
          checked={selected.length === OUTPUT_TYPES[type].options.length}
          onChange={selectType}
          showAdded={false}
        >
          {options.map((el) => {
            return (
              <Row key={el.value}>
                <Col>
                  <input
                    id={el.value}
                    name={el.value}
                    data-testid={el.value}
                    type="checkbox"
                    value={el.value}
                    checked={selected.includes(el.value)}
                    onChange={onChange}
                  />
                  <label className="text-normal link" htmlFor={el.value}>
                    {el.label}
                  </label>
                </Col>
              </Row>
            );
          })}
        </CollapseTable>
      ) : (
        <div key={`type-${OUTPUT_TYPES[type].label}`} className={`hydrology-table`}>
          <input
            id={type}
            data-testid={type}
            type="radio"
            name="outputFilesFormat"
            value={type}
            checked={selectedType === type}
            onChange={selectType}
          />
          <label htmlFor={type}>{OUTPUT_TYPES[type].label}</label>
        </div>
      )}
    </>
  );
});
